import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { CartesianGrid, Legend, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useAppSelector } from '../../../../../reduxService/hooks';
import { selectProject, selectResults, selectStillingBasinAndSoilDataByProject } from '../../../selector/conversion';
import { selectProjectInfo } from '../../../../store/projectInfo/projectInfoSlice';
import { ISlopedGabionResults } from '../../../api/IApiResponse';
import { roundToTwo } from '../../../../MACRA_1/components/InputForm/Geometry/utils';
import { conversionLabel } from '../../../../conversion';

interface IStressResultsProps {}

interface ICustomTooltip {
    active?: boolean;
    payload?: any;
}

const StressResults = (props: IStressResultsProps) => {
    const { t } = useTranslation();

    const results = useAppSelector(selectResults);
    const projectInfo = useAppSelector(selectProjectInfo);
    const stillingBasinAndSoilData = useAppSelector(selectStillingBasinAndSoilDataByProject);

    let stressData = (results.data as ISlopedGabionResults).hydraulic.plotPoints
        .filter(x => x.isSloping)
        .map(value => ({
            x: roundToTwo(value.lunghezzaBrigliaX),
            y: roundToTwo(value.stress),
        }));

    let allowedStressData = [
        {
            x: 0,
            y: stillingBasinAndSoilData?.stillingBasin?.material.physicalData![0].allowShearStress,
        },
        {
            x: stressData[stressData.length - 1].x,
            y: stillingBasinAndSoilData?.stillingBasin?.material.physicalData![0].allowShearStress,
        },
    ];

    const CustomTooltip = (props: ICustomTooltip) => {
        if (props.active && props.payload && props.payload.length) {
            return (
                <div className='discharge-curve-tooltip'>
                    <span>{`${t('horizontal_distance_after_the_crest')}: ${props.payload[0].payload.x} ${conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number]}`}</span>
                    <span>{`${t('stress')}: ${props.payload[0].payload.y} ${conversionLabel.Nm2_lbfft2[projectInfo.data?.unitOfMeasure as number]}`}</span>
                </div>
            );
        }
        return null;
    };

    return (
        <div className={'d-flex w-100 h-85 align-items-center justify-content-center'}>
            {!!stressData && stressData.length > 0 && (
                <ResponsiveContainer width='70%' height='70%'>
                    <LineChart
                        id='currentChart'
                        width={500}
                        height={300}
                        margin={{
                            top: 5,
                            right: 30,
                            left: 20,
                            bottom: 5,
                        }}>
                        <CartesianGrid strokeDasharray='3 3' />
                        <YAxis
                            dataKey='y'
                            domain={['auto', 'auto']}
                            type='number'
                            interval={0}
                            label={{
                                value: `${t('stress')} (${conversionLabel.Nm2_lbfft2[projectInfo.data?.unitOfMeasure as number]})`,
                                style: { textAnchor: 'middle' },
                                angle: -90,
                                position: 'left',
                                offset: -17,
                            }}
                            allowDataOverflow={true}
                        />
                        <XAxis
                            dataKey='x'
                            domain={['auto', 'auto']}
                            interval={0}
                            type='number'
                            label={{
                                key: 'xAxisLabel',
                                value: `${t('horizontal_distance_after_the_crest')} (${conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number]})`,
                                position: 'bottom',
                                offset: -7,
                            }}
                            allowDataOverflow={true}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend verticalAlign='top' height={36} />
                        <Line strokeWidth={2} name={`${t('applied_stress')}`} dot={false} data={stressData} type='linear' dataKey='y' stroke='#004652' tooltipType='none' />
                        <Line strokeWidth={2} name={`${t('allowable_stress_slope')}`} dot={false} data={allowedStressData} type='linear' dataKey='y' stroke='#22db22' tooltipType='none' />
                    </LineChart>
                </ResponsiveContainer>
            )}
        </div>
    );
};

export default StressResults;
